/**
 * Agency API Endpoints
 * Handles agency registration, onboarding, and management
 */

import { axiosInstance } from '../axios.config';
import type {
  Agency,
  AgencyRegistrationData,
  AgencyUpdateData,
  AgencyListResponse,
  AgencyQueryParams,
  AgencyVerificationRequest,
  AgencyStats,
  AgencyDocument,
  AgencyCompleteProfile,
  AgencyInfoData,
  AgencyAddressData,
  AgencyLicensingData,
  AgencyServicesData,
  AgencyOperationsData,
  AgencyInsuranceData,
  AgencyDocumentsData,
  AgencyProfileData,
} from '@/types/agency.types';

/**
 * Agency API
 */
export const agencyApi = {
  /**
   * Register a new agency (Public endpoint - no auth required)
   */
  registerAgency: async (data: AgencyRegistrationData): Promise<Agency> => {
    const response = await axiosInstance.post<Agency>('/agencies/register', data);
    return response.data;
  },

  /**
   * Get paginated list of agencies (Admin only)
   */
  getAgencies: async (params?: AgencyQueryParams): Promise<AgencyListResponse> => {
    const response = await axiosInstance.get<AgencyListResponse>('/agencies', { params });
    return response.data;
  },

  /**
   * Get single agency by ID
   */
  getAgencyById: async (id: string): Promise<Agency> => {
    const response = await axiosInstance.get<Agency>(`/agencies/${id}`);
    return response.data;
  },

  /**
   * Update agency information
   */
  updateAgency: async (id: string, data: AgencyUpdateData): Promise<Agency> => {
    const response = await axiosInstance.put<Agency>(`/agencies/${id}`, data);
    return response.data;
  },

  /**
   * Delete/Deactivate agency
   */
  deleteAgency: async (id: string): Promise<{ message: string }> => {
    const response = await axiosInstance.delete<{ message: string }>(`/agencies/${id}`);
    return response.data;
  },

  /**
   * Verify agency (Admin only)
   */
  verifyAgency: async (data: AgencyVerificationRequest): Promise<Agency> => {
    const response = await axiosInstance.post<Agency>(
      `/agencies/${data.agencyId}/verify`,
      data
    );
    return response.data;
  },

  /**
   * Upload agency document
   */
  uploadDocument: async (
    agencyId: string,
    file: File,
    documentType: string
  ): Promise<AgencyDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);

    const response = await axiosInstance.post<AgencyDocument>(
      `/agencies/${agencyId}/documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Get agency documents
   */
  getDocuments: async (agencyId: string): Promise<AgencyDocument[]> => {
    const response = await axiosInstance.get<AgencyDocument[]>(
      `/agencies/${agencyId}/documents`
    );
    return response.data;
  },

  /**
   * Delete agency document
   */
  deleteDocument: async (agencyId: string, documentId: string): Promise<{ message: string }> => {
    const response = await axiosInstance.delete<{ message: string }>(
      `/agencies/${agencyId}/documents/${documentId}`
    );
    return response.data;
  },

  /**
   * Get agency statistics (Admin only)
   */
  getAgencyStats: async (): Promise<AgencyStats> => {
    const response = await axiosInstance.get<AgencyStats>('/agencies/stats');
    return response.data;
  },

  /**
   * Check if agency name is available
   */
  checkAgencyNameAvailability: async (agencyName: string): Promise<{ available: boolean }> => {
    const response = await axiosInstance.get<{ available: boolean }>(
      '/agencies/check-name',
      {
        params: { agencyName },
      }
    );
    return response.data;
  },

  /**
   * Resend verification email
   */
  resendVerificationEmail: async (agencyId: string): Promise<{ message: string }> => {
    const response = await axiosInstance.post<{ message: string }>(
      `/agencies/${agencyId}/resend-verification`
    );
    return response.data;
  },

  /**
   * Get my agency (for logged-in agency users)
   */
  getMyAgency: async (): Promise<Agency> => {
    const response = await axiosInstance.get<Agency>('/agencies/me');
    return response.data;
  },

  /**
   * Update my agency (for logged-in agency users)
   */
  updateMyAgency: async (data: AgencyUpdateData): Promise<Agency> => {
    const response = await axiosInstance.put<Agency>('/agencies/me', data);
    return response.data;
  },

  // ============================================
  // PHASE 0 - EXTENDED PROFILE ENDPOINTS
  // ============================================

  /**
   * Get complete agency profile with all onboarding data
   */
  getAgencyProfile: async (agencyId: string): Promise<AgencyCompleteProfile> => {
    const response = await axiosInstance.get<{ success: boolean; data: AgencyCompleteProfile }>(
      `/api/v1/agencies/${agencyId}/profile`
    );
    return response.data.data;
  },

  /**
   * Get my complete profile (for logged-in agency users)
   */
  getMyProfile: async (): Promise<AgencyCompleteProfile> => {
    const response = await axiosInstance.get<{ success: boolean; data: AgencyCompleteProfile }>(
      '/api/v1/agencies/me/profile'
    );
    return response.data.data;
  },

  /**
   * Draft revision status for live profile edits
   */
  getAgencyRevision: async (agencyId: string): Promise<any> => {
    const response = await axiosInstance.get<{ success: boolean; data: any }>(
      `/api/v1/agencies/${agencyId}/revision`
    );
    return response.data.data;
  },

  /**
   * Submit draft revision for admin review (locks edits)
   */
  submitAgencyRevision: async (agencyId: string): Promise<any> => {
    const response = await axiosInstance.post<{ success: boolean; data: any; message?: string }>(
      `/api/v1/agencies/${agencyId}/revision/submit`
    );
    return response.data;
  },

  /**
   * Update agency info (Step 1) - NEW API STRUCTURE
   */
  updateAgencyInfo: async (agencyId: string, data: Partial<AgencyInfoData>): Promise<Agency> => {
    const commPhone = data.communicationPhone ?? data.mainPhone;
    const commEmail = data.communicationEmail ?? data.mainEmail;
    const requestBody = {
      agencyInfo: {
        agencyName: data.agencyName,
        legalBusinessName: data.legalBusinessName,
        agencyType: data.agencyType,
        einTaxId: data.einTaxId ?? data.taxId,
        communicationPhone: commPhone,
        communicationEmail: commEmail,
        mainPhoneNumber: commPhone,
        faxNumber: data.fax,
        mainEmailAddress: commEmail,
        website: data.website,
        contactFirstName: (data as any).contactFirstName,
        contactLastName: (data as any).contactLastName,
        contactEmail: (data as any).contactEmail,
        contactPhone: (data as any).contactPhone,
      },
    };

    const response = await axiosInstance.put<Agency>(
      `/api/v1/agencies/${agencyId}/steps/agency-info`,
      requestBody
    );
    return response.data;
  },

  /**
   * Update address (Step 2) - NEW API STRUCTURE
   */
  updateAgencyAddress: async (
    agencyId: string,
    data: Partial<AgencyAddressData>
  ): Promise<Agency> => {
    // Transform data to match API structure
    const requestBody = {
      addresses: {
        physicalOffice: {
          street: data.street || '',
          street2: data.street2 || undefined,
          city: data.city || '',
          state: data.state || '',
          zip: data.zipCode || '',
          location: data.physicalLocation || undefined,
        },
        mailing: !data.mailingAddressSame && data.mailingAddress ? {
          street: data.mailingAddress.street || '',
          street2: data.mailingAddress.street2 || undefined,
          city: data.mailingAddress.city || '',
          state: data.mailingAddress.state || '',
          zip: data.mailingAddress.zipCode || '',
          location: data.mailingLocation || undefined,
        } : undefined,
        mailingSameAsPhysical: data.mailingAddressSame ?? true,
      },
    };

    const response = await axiosInstance.put<Agency>(
      `/api/v1/agencies/${agencyId}/steps/address`,
      requestBody
    );
    return response.data;
  },

  /**
   * Update licensing (Step 4) - NEW API STRUCTURE
   */
  updateAgencyLicensing: async (
    agencyId: string,
    data: Partial<AgencyLicensingData>
  ): Promise<Agency> => {
    // Transform data to match API structure
    const requestBody = {
      licensing: {
        credentials: {
          npiNumber: data.npiNumber || '',
          licenseNumber: data.stateLicenseNumber || '',
          expirationDate: data.stateLicenseExpiration || '',
        },
        medicare: {
          participation: data.medicareCertified ?? false,
          medicareCcn: data.medicareCertified
            ? (data.medicareCCN ? String(data.medicareCCN).replace(/\D/g, '').slice(0, 6) : undefined)
            : undefined,
        },
        medicaid: {
          participation: data.medicaidCertified ?? false,
          medicaidProviderId: data.medicaidCertified ? (data.medicaidNumber || undefined) : undefined,
        },
        professionalAccreditations: data.accreditations?.map((acc) => ({
          organization: acc.type || '',
          certNumber: acc.certificateNumber || undefined,
          expires: acc.expirationDate || undefined,
        })) || [],
      },
    };

    const response = await axiosInstance.put<Agency>(
      `/api/v1/agencies/${agencyId}/steps/licensing`,
      requestBody
    );
    return response.data;
  },

  /**
   * Update services (Step 5) - NEW API STRUCTURE
   */
  updateAgencyServices: async (
    agencyId: string,
    data: Partial<AgencyServicesData>
  ): Promise<Agency> => {
    const counties = data.counties || data.countiesRegions || [];
    const countiesRegions = data.countiesRegions || data.counties || [];
    const cities = data.cities || [];
    const state = data.state || data.states?.[0];
    const states = state ? [state] : (data.states || []);

    // Transform data to match API structure
    const requestBody = {
      services: {
        clinicalServices: data.servicesOffered || [],
        coverage: {
          zipCodesServed: data.serviceAreas || [],
          cities,
          counties,
          states,
          countiesRegions,
          serviceRadiusMiles: data.serviceRadius ?? 20,
        },
        languagesSpokenByClinicalStaff: data.languagesSpoken || [],
      },
    };

    const response = await axiosInstance.put<Agency>(
      `/api/v1/agencies/${agencyId}/steps/services`,
      requestBody
    );
    return response.data;
  },

  /**
   * Update operations (Step 6) - NEW API STRUCTURE
   */
  updateAgencyOperations: async (
    agencyId: string,
    data: Partial<AgencyOperationsData>
  ): Promise<Agency> => {
    // Transform weekSchedule array into operatingHours object
    const operatingHoursObj: Record<string, any> = {};
    
    if (data.weekSchedule && Array.isArray(data.weekSchedule)) {
      data.weekSchedule.forEach((daySchedule: any) => {
        const dayKey = daySchedule.day.toLowerCase(); // 'Monday' -> 'monday'
        
        if (daySchedule.closed) {
          // If closed, only send isClosed: true
          operatingHoursObj[dayKey] = {
            isClosed: true
          };
        } else {
          // Format times from 24-hour (HH:mm) to 12-hour (h:mm AM/PM)
          const formatTime = (time: string) => {
            const [hours, minutes] = time.split(':');
            const hour = parseInt(hours);
            const ampm = hour >= 12 ? 'PM' : 'AM';
            const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
            return `${displayHour}:${minutes} ${ampm}`;
          };
          
          operatingHoursObj[dayKey] = {
            open: formatTime(daySchedule.open),
            close: formatTime(daySchedule.close),
            isClosed: false
          };
        }
      });
    }
    
    const requestBody = {
      operations: {
        operatingHours: operatingHoursObj,
        emergencyOnCallService: data.emergencyAvailable ?? false,
        staffInformation: {
          totalNumberOfEmployees: data.numberOfEmployees || 0,
          licensedClinicalStaff: data.numberOfLicensedStaff || 0,
        },
      },
    };
    
    console.log('📤 Step 6 (Operations) API Request:', JSON.stringify(requestBody, null, 2));

    const response = await axiosInstance.put<Agency>(
      `/api/v1/agencies/${agencyId}/steps/operations`,
      requestBody
    );
    return response.data;
  },

  /**
   * Update insurance (Step 7) - NEW API STRUCTURE
   */
  updateAgencyInsurance: async (
    agencyId: string,
    data: Partial<AgencyInsuranceData>
  ): Promise<Agency> => {
    const requestBody = {
      insurance: {
        professionalLiabilityInsurance: {
          insuranceCarrier: data.liabilityInsurance?.carrierId || '',
          insuranceProvider: data.liabilityInsurance?.provider || '',
          policyNumber: data.liabilityInsurance?.policyNumber || '',
          expirationDate: data.liabilityInsurance?.expirationDate || '',
          coverageAmount: data.liabilityInsurance?.coverageAmount || 0,
        },
        workersCompensation: {
          insuranceCarrier: data.workersCompInsurance?.carrierId || '',
          provider: data.workersCompInsurance?.provider || '',
          policyNumber: data.workersCompInsurance?.policyNumber || '',
          expirationDate: data.workersCompInsurance?.expirationDate || '',
        },
        carriers: (data.carriers || []).map(carrier => ({
          carrierId: carrier.carrierId,
          carrierName: carrier.carrierName || '',
          plans: (carrier.plans || []).map(plan => ({
            planId: plan.planId,
            planName: plan.planName || '',
            carrierId: carrier.carrierId,
          })),
        })),
        taxonomyCode: data.taxonomyCode || '',
      },
    };

    console.log('📤 Step 7 (Insurance) API Request:', JSON.stringify(requestBody, null, 2));

    const response = await axiosInstance.put<Agency>(
      `/api/v1/agencies/${agencyId}/steps/insurance`,
      requestBody
    );
    return response.data;
  },

  /**
   * Update documents (Step 8) - NEW API STRUCTURE
   */
  updateAgencyDocuments: async (
    agencyId: string,
    data: Partial<AgencyDocumentsData>
  ): Promise<Agency> => {
    // Transform documents array to keyed object structure
    const documentsObj: Record<string, any> = {};
    
    if (data.documents && Array.isArray(data.documents)) {
      data.documents.forEach((doc: any) => {
        // Map frontend document keys to backend keys
        const keyMap: Record<string, string> = {
          'stateLicense': 'stateHomeHealthLicense',
          'liabilityInsurance': 'liabilityInsuranceCertificate',
          'medicareCertification': 'medicareCertification',
          'medicaidCertification': 'medicaidCertification',
          'accreditation': 'accreditationCertificate',
          'w9Form': 'w9Form',
        };
        
        const backendKey = keyMap[doc.documentType] || doc.documentType;
        
        // ✅ CRITICAL: Ensure key field is present
        const s3Key = doc.key || doc.s3Key;
        if (!s3Key) {
          console.error('❌ Missing key field for document:', doc);
          throw new Error(`Document ${doc.fileName || 'unknown'} is missing required 'key' field`);
        }
        
        // Structure document metadata for backend with all required fields
        documentsObj[backendKey] = {
          fileName: doc.fileName || doc.name,
          bucket: doc.bucket || '',
          key: s3Key, // ✅ Required field
          baseUrl: doc.baseUrl || '',
          cdnUrl: doc.cdnUrl || doc.url || '',
          contentType: doc.contentType || doc.type || 'application/pdf',
          fileSize: doc.fileSize || doc.size || 0,
          s3Url: doc.s3Url || doc.url || '',
        };
      });
    }

    // If caller tracked removed doc types, explicitly null them out so backend can unset.
    // Some implementations merge `documents` objects and won't delete absent keys.
    const removedDocumentTypes: string[] = Array.isArray((data as any).removedDocumentTypes)
      ? (data as any).removedDocumentTypes
      : [];
    if (removedDocumentTypes.length > 0) {
      const keyMap: Record<string, string> = {
        stateLicense: 'stateHomeHealthLicense',
        liabilityInsurance: 'liabilityInsuranceCertificate',
        medicareCertification: 'medicareCertification',
        medicaidCertification: 'medicaidCertification',
        accreditation: 'accreditationCertificate',
        w9Form: 'w9Form',
      };
      removedDocumentTypes.forEach((docType) => {
        const backendKey = keyMap[docType] || docType;
        documentsObj[backendKey] = null;
      });
    }
    
    const requestBody = {
      documents: documentsObj,
    };

    console.log('📤 Step 8 (Documents) API Request:', JSON.stringify(requestBody, null, 2));

    const response = await axiosInstance.put<Agency>(
      `/api/v1/agencies/${agencyId}/steps/documents`,
      requestBody
    );
    return response.data;
  },

  /**
   * Update profile & marketing (Step 2 in live wizard) - NEW API STRUCTURE
   */
  updateAgencyProfileMarketing: async (
    agencyId: string,
    data: Partial<AgencyProfileData>
  ): Promise<Agency> => {
    // Transform frontend data to backend structure
    const requestBody = {
      profile: {
        tagline: data.tagline || '',
        publicDescription: data.description || '',
        // @ts-ignore - logo metadata stored during upload
        logo: data.logoMetadata ? {
          // @ts-ignore
          fileName: data.logoMetadata.fileName,
          // @ts-ignore
          bucket: data.logoMetadata.bucket,
          // @ts-ignore
          key: data.logoMetadata.key,
          // @ts-ignore
          baseUrl: data.logoMetadata.baseUrl,
          // @ts-ignore
          cdnUrl: data.logoMetadata.cdnUrl,
          // @ts-ignore
          contentType: data.logoMetadata.contentType,
          // @ts-ignore
          fileSize: data.logoMetadata.fileSize,
          // @ts-ignore
          s3Url: data.logoMetadata.s3Url,
        } : undefined,
        // @ts-ignore - photos metadata stored during upload
        photos: (data.photosMetadata || []).map((photo: any) => ({
          fileName: photo.fileName,
          bucket: photo.bucket,
          key: photo.key,
          baseUrl: photo.baseUrl,
          cdnUrl: photo.cdnUrl,
          contentType: photo.contentType,
          fileSize: photo.fileSize,
          s3Url: photo.s3Url,
        })),
        socialMedia: (() => {
          const socialMedia: any = {};
          
          // Only include URLs if they are not empty
          if (data.socialMedia?.facebook && data.socialMedia.facebook.trim() !== '') {
            socialMedia.facebookUrl = data.socialMedia.facebook.trim();
          }
          if (data.socialMedia?.linkedin && data.socialMedia.linkedin.trim() !== '') {
            socialMedia.linkedInUrl = data.socialMedia.linkedin.trim();
          }
          if (data.socialMedia?.twitter && data.socialMedia.twitter.trim() !== '') {
            socialMedia.twitterUrl = data.socialMedia.twitter.trim();
          }
          if (data.socialMedia?.instagram && data.socialMedia.instagram.trim() !== '') {
            socialMedia.instagramUrl = data.socialMedia.instagram.trim();
          }
          
          return socialMedia;
        })(),
      },
    };

    console.log('📤 Step 10 (Profile) API Request:', JSON.stringify(requestBody, null, 2));

    const response = await axiosInstance.put<Agency>(
      `/api/v1/agencies/${agencyId}/steps/profile`,
      requestBody
    );
    return response.data;
  },

  /**
   * Complete onboarding process - NEW API STRUCTURE
   */
  completeOnboarding: async (agencyId: string): Promise<AgencyCompleteProfile> => {
    const response = await axiosInstance.post<AgencyCompleteProfile>(
      `/api/v1/agencies/${agencyId}/complete-onboarding`
    );
    return response.data;
  },

  /**
   * Resubmit agency profile after admin revision request
   */
  resubmitProfile: async (agencyId: string, comments?: string): Promise<Agency> => {
    const response = await axiosInstance.put<Agency>(
      `/api/v1/agencies/${agencyId}/resubmit`,
      { comments: comments || '' }
    );
    return response.data;
  },

  /**
   * Upload profile photo (logo, office, staff, care)
   */
  uploadProfilePhoto: async (
    agencyId: string,
    file: File,
    photoType: 'logo' | 'office' | 'staff' | 'care'
  ): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('photoType', photoType);

    const response = await axiosInstance.post<{ url: string }>(
      `/agencies/${agencyId}/profile/photos`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },
};

export default agencyApi;
